/**
 * x402 agent client — streams the AgentCore SSE response from a single chat call.
 *
 * Transport is plain `fetch` + ReadableStream (NOT socket.io): AgentCore Runtime
 * streams Server-Sent Events natively over HTTP, which is the modern AWS-native
 * transport. Locally the CRA dev server proxies /api/wafmon/agent → the agent on :8080
 * (see setupProxy.js); when deployed, prod-env points at the invoker CloudFront URL.
 *
 * The frame protocol mirrors the reference web-ui (realBuyer.ts): events are
 * separated by a blank line, each carrying one or more `data:` lines whose JSON
 * payload has a `kind`:
 *   assistant   — streamed prose delta
 *   reasoning|request|payment|result|fallback — buyer flow events (drive the diagram)
 *   sellerLog   — seller-side log row
 *   reveal      — a tool finished (content fetched)
 *   suggestions — follow-up chips
 *   discovered  — what the agent discovered this turn (sellers + skills)
 *   done        — terminal; carries the final outcome
 */

/** Buyer-flow event kinds the diagram + log render from. */
export const FLOW_KINDS = ['reasoning', 'request', 'payment', 'result', 'fallback'];

const DEFAULT_BOT_UA =
  'Mozilla/5.0 (compatible; Bedrockbot/1.0; +https://developer.amazon.com/bedrock)';

/** Hex SHA-256 of a string, via SubtleCrypto. Used for the CloudFront-OAC
 *  `x-amz-content-sha256` header on POSTs to the Lambda Function URL invoker.
 *  Returns '' if SubtleCrypto is unavailable (e.g. non-secure localhost) — the local
 *  CRA proxy doesn't need the header, so that's fine. */
async function sha256Hex(str) {
  try {
    const subtle = (typeof crypto !== 'undefined' && crypto.subtle) || null;
    if (!subtle) return '';
    const buf = await subtle.digest('SHA-256', new TextEncoder().encode(str));
    return Array.from(new Uint8Array(buf))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
  } catch {
    return '';
  }
}

/**
 * Send one chat message and stream events to the handler callbacks.
 *
 * @param {object} opts
 * @param {string} opts.invokerUrl          chat endpoint (local proxy path or deployed URL)
 * @param {string} opts.message             the user's message
 * @param {string} opts.sellerBaseUrl       seller base the agent hits — THE TOGGLE drives this
 * @param {string} [opts.botUA]             user-agent the agent presents (bot → priced edge)
 * @param {string} [opts.maxPayUsdc]
 * @param {string} [opts.sessionId]
 * @param {Array}  [opts.history]           prior turns ([{role, content:[{text}]}])
 * @param {boolean}[opts.usePortal]         discovery master switch (default true)
 * @param {AbortSignal} [opts.signal]
 * @param {object} handlers                 { onAssistantDelta, onEvent, onSellerLog,
 *                                            onReveal, onSuggestions, onDiscovered, onDone }
 */
export async function chatWithAgent(opts, handlers = {}) {
  const {
    invokerUrl,
    message,
    sellerBaseUrl,
    botUA = DEFAULT_BOT_UA,
    maxPayUsdc = '0.10',
    sessionId = '',
    history = [],
    usePortal = true,
    // Discovery allow-list: seller ids the agent may use this turn. undefined → all.
    enabledSellers,
    // Session-scoped edge posture: 'monetized' | 'blocked'. Sent per chat call so
    // each browser session's toggle is isolated (local seller honors it per-request).
    posture = 'monetized',
    signal,
  } = opts;

  const h = {
    onAssistantDelta() {},
    onEvent() {},
    onSellerLog() {},
    onReveal() {},
    onSuggestions() {},
    onDiscovered() {},
    onDone() {},
    ...handlers,
  };

  const headers = {
    'content-type': 'application/json',
    accept: 'text/event-stream',
  };

  // Serialize the body once so we can hash it for the OAC header below.
  const bodyStr = JSON.stringify({
    flowId: 'shop',
    message,
    sellerBaseUrl,
    history,
    sessionId,
    botUA,
    maxPayUsdc,
    usePortal,
    // Only send the allow-list when set (undefined → agent uses all sellers).
    ...(enabledSellers ? { enabledSellers } : {}),
    posture,
  });

  // DEPLOYED: the invoker is a Lambda Function URL behind CloudFront OAC (SigV4). For
  // POST bodies, CloudFront requires the caller to send the SHA-256 of the body in
  // `x-amz-content-sha256` — Lambda Function URLs don't accept unsigned payloads. We
  // compute it client-side via SubtleCrypto. Local dev (CRA proxy) ignores the header.
  const sha = await sha256Hex(bodyStr);
  if (sha) headers['x-amz-content-sha256'] = sha;

  let r;
  try {
    r = await fetch(invokerUrl, {
      method: 'POST',
      headers,
      body: bodyStr,
      signal,
    });
  } catch (err) {
    if (err?.name === 'AbortError') {
      h.onDone('aborted');
      return;
    }
    h.onEvent({ id: 'err', ts: Date.now(), kind: 'fallback', text: `Agent unreachable: ${err.message}` });
    h.onDone('lost');
    return;
  }

  if (!r.ok || !r.body) {
    h.onEvent({ id: 'err', ts: Date.now(), kind: 'fallback', text: `Agent returned HTTP ${r.status}` });
    h.onDone('lost');
    return;
  }

  const reader = r.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  // Mutable holder so dispatch() can set the outcome without a closure over a
  // loop-scoped variable (avoids the no-loop-func lint). `fireDone` fires the
  // caller's onDone EXACTLY once — at the `done` frame, so the UI unlocks while
  // the stream stays open for the late `suggestions` frame.
  const state = {
    outcome: 'served-free',
    doneFired: false,
    fireDone(outcome) {
      if (this.doneFired) return;
      this.doneFired = true;
      h.onDone(outcome);
    },
  };
  let sawFrame = false;

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let sep;
    while ((sep = buffer.indexOf('\n\n')) !== -1) {
      const frame = buffer.slice(0, sep);
      buffer = buffer.slice(sep + 2);
      const json = frame
        .split('\n')
        .filter((l) => l.startsWith('data:'))
        .map((l) => l.slice(l.startsWith('data: ') ? 6 : 5))
        .join('\n')
        .trim();
      if (!json) continue;
      sawFrame = true;
      let evt;
      try {
        evt = JSON.parse(json);
      } catch {
        continue;
      }
      dispatch(evt, h, state);
    }
  }

  // The invoker may commit HTTP 200 then write a non-SSE JSON error body. If we
  // never saw a frame, surface the leftover buffer as the error — never fail silently.
  if (!sawFrame) {
    let msg = buffer.trim();
    try {
      msg = JSON.parse(msg).error ?? msg;
    } catch {
      /* keep raw */
    }
    h.onEvent({ id: 'err', ts: Date.now(), kind: 'fallback', text: `Agent error: ${msg || 'empty response'}` });
    state.outcome = 'lost';
  }
  // Backstop for streams that ended without a `done` frame (error paths); fire-once.
  state.fireDone(state.outcome);
}

function dispatch(evt, h, state) {
  switch (evt.kind) {
    case 'assistant':
      // reset:true retracts everything streamed so far this turn — the agent
      // streamed speculative (ungrounded) prose before calling a tool.
      h.onAssistantDelta(evt.text ?? '', { reset: !!evt.reset });
      break;
    case 'sellerLog':
      h.onSellerLog(evt);
      break;
    case 'reveal':
      h.onReveal(evt.action, evt.outcome ?? 'served-free', evt.content, {
        cardType: evt.cardType,
        domain: evt.domain,
        sellerId: evt.sellerId,
        skillId: evt.skillId,
      });
      break;
    case 'suggestions':
      h.onSuggestions(Array.isArray(evt.items) ? evt.items : []);
      break;
    case 'discovered':
      h.onDiscovered({
        usePortal: !!evt.usePortal,
        mode: String(evt.mode ?? ''),
        sellers: Array.isArray(evt.sellers) ? evt.sellers : [],
      });
      break;
    case 'done':
      state.outcome = evt.outcome ?? 'served-free';
      // Unlock the UI NOW — chips (a `suggestions` frame) may still arrive on the
      // open stream after this.
      state.fireDone(state.outcome);
      break;
    default:
      if (evt.kind && FLOW_KINDS.includes(evt.kind)) h.onEvent(evt);
  }
}
